import React from 'react';
import { useKoala } from '#/vendors/koala';
import { usePostHog } from '#/vendors/posthog';

import type { AnalyticsCapture, AnalyticsEventData } from './types';
import { serializeEventName } from './utils';

/**
 * Get the analytics library, via React hooks.
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const { koala } = useKoala();

  /*
  |------------------
  | Utils
  |------------------
  */

  /**
   * Collect the base event data.
   */
  const collectBaseEventData = React.useCallback(() => {
    return {
      application: 'quarks',
      sessionId: posthog.get_distinct_id(),
    };
  }, [posthog]);

  /*
  |------------------
  | Overrides & Customizations
  |------------------
  */

  const captureException = React.useCallback(
    (error: unknown, data?: AnalyticsEventData) => {
      try {
        const err = error instanceof Error ? error : new Error('Unknown error');
        posthog.captureException(err, {
          name: err.name,
          message: err.message,
          stack: err.stack,
          ...data,
        });

        koala?.trackError(err);
      } catch (error) {
        console.error('Failed to capture exception', error);
      }
    },
    [posthog, koala]
  );

  const capture: AnalyticsCapture = React.useCallback(
    async (eventName, eventData) => {
      const collectedData = collectBaseEventData();
      const serializedEventName = serializeEventName(eventName);

      try {
        posthog.capture(serializedEventName, {
          ...eventData,
          ...collectedData,
        });

        void koala?.track(serializedEventName, {
          ...eventData,
          ...collectedData,
        });
      } catch (error) {
        captureException(error);
      }
    },
    [posthog, koala, collectBaseEventData, captureException]
  );

  const identify = React.useCallback(
    (payload: { email: string }) => {
      try {
        if (posthog) {
          posthog.identify(posthog.get_distinct_id(), {
            email: payload.email,
          });
        }

        if (koala) {
          void koala.identify(payload.email, {
            posthogId: posthog.get_distinct_id(),
          });
        }
      } catch (err) {
        captureException(err);
      }
    },
    [posthog, koala, captureException]
  );

  return {
    capture,
    captureException,
    identify,
    getSessionReplayURL: () => posthog.get_session_replay_url(),
    getSessionId: () => posthog.get_session_id(),
    getUserID: () => posthog.get_distinct_id(),
  };
}
